import { publicationUploadMaxSize } from 'src/constants';
import { CrfVersion } from './CrfVersion';
import { V2026_BASE } from './endpoints';
import type {
  AcrFileColumn,
  CommentOrdering,
  NarrativeLayout,
  PublicationReport,
  PublicationResultColumn,
  PublicationStatus,
  WorkspaceVariant,
} from './types';

export class Crf20262029 extends CrfVersion {
  readonly code = 'CRF 2026-2029';

  get workspaceVariant(): WorkspaceVariant {
    return 'acr2023';
  }

  get narrativeLayout(): NarrativeLayout {
    return 'multi-narrative';
  }

  get commentOrdering(): CommentOrdering {
    return 'multi-narrative-grouped';
  }

  get viewCommentsLabel(): string {
    return 'View all narratives and comments';
  }

  get fileColumns(): AcrFileColumn[] {
    return [
      { type: 'acr_5', header: 'ACR 5', accessor: 'acr_5_file_url' },
      { type: 'acr_1', header: 'ACR 1', accessor: 'acr_1_file_url' },
      { type: 'acr_4', header: 'ACR 4', accessor: 'acr_4_file_url' },
    ];
  }

  get resultColumns(): PublicationResultColumn[] {
    return [
      { kind: 'publicLink', header: 'Public link', accessor: 'public_link' },
      {
        kind: 'restrictedLink',
        header: 'Restricted link',
        accessor: 'restricted_link',
      },
    ];
  }

  get supportsMultiUpload(): boolean {
    return false;
  }

  get statusColumnHeader(): string {
    return 'Publication Status';
  }

  get actionLabel(): string {
    return 'Publish ready';
  }

  get actionTestId(): string {
    return 'publish-ready-button';
  }

  get actionStartedResponse(): string {
    return 'Publication started';
  }

  get actionStartedMessage(): string {
    return 'Publication started';
  }

  get actionCompletedMessage(): string {
    return 'Publication completed';
  }

  get pendingFlagKey(): string {
    return 'waiting_publication_result';
  }

  get confirmationHeading(): string {
    return 'Confirm Publication';
  }

  get confirmationIntro(): string {
    return 'These ACRs will be published:';
  }

  get confirmationQuestion(): string {
    return 'Do you want to proceed with the publication?';
  }

  listUrl(): string {
    return V2026_BASE;
  }

  startActionUrl(): string {
    return `${V2026_BASE}submit/`;
  }

  statusUrl(): string {
    return `${V2026_BASE}status/`;
  }

  calcStatus(report: PublicationReport): PublicationStatus {
    const {
      current_status,
      acr_1_file_url,
      acr_4_file_url,
      acr_5_file_url,
      public_link,
      restricted_link,
    } = report;

    if (public_link || restricted_link) return 'Done';

    if (
      current_status !== 'published' ||
      !acr_1_file_url ||
      !acr_4_file_url ||
      !acr_5_file_url
    ) {
      return 'Not ready';
    }

    return 'Ready';
  }

  validateFile(file: File): string {
    if (file?.name?.split('.').pop() !== 'pdf') return 'file_type';

    if (file?.size / 1024 / 1024 > publicationUploadMaxSize) return 'file_size';

    return '';
  }
}
